/**
 * Sticky Buy Button - Slides in after scrolling past product info
 */
class StickyBuyButton {
  constructor() {
    this.stickyButton = document.querySelector("[data-sticky-buy-button]");
    if (!this.stickyButton) return;

    this.scrollThreshold = 400; // Scroll amount in px before showing
    this.hasBeenShown = false;
    this.isVisible = false;
    this.footerVisible = false;
    this.variantChangeUnsubscriber = null;
    this.footerObserver = null;

    this.init();
  }

  destroy() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
    }
  }

  init() {
    this.setupScrollListener();
    this.setupFooterObserver();
    this.setupVariantSync();
    this.setupFormHandler();
  }

  setupScrollListener() {
    let ticking = false;

    window.addEventListener("scroll", () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  setupFooterObserver() {
    const footer = document.querySelector(
      'footer, .footer, [data-section-type="footer"]'
    );
    if (!footer) return;

    this.footerObserver = new IntersectionObserver(
      (entries) => {
        this.footerVisible = entries[0].isIntersecting;
        if (this.footerVisible) {
          this.hide();
        } else if (window.scrollY > this.scrollThreshold) {
          this.show();
        }
      },
      { threshold: 0, rootMargin: "0px 0px 200px 0px" }
    );

    this.footerObserver.observe(footer);
  }

  handleScroll() {
    const scrollY = window.scrollY;

    if (scrollY > this.scrollThreshold && !this.footerVisible) {
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    if (!this.isVisible) {
      this.stickyButton.classList.add("is-visible");
      this.isVisible = true;
      this.hasBeenShown = true;
    }
  }

  hide() {
    if (this.isVisible) {
      this.stickyButton.classList.remove("is-visible");
      this.isVisible = false;
    }
  }

  setupVariantSync() {
    this.variantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.variantChange,
      this.handleVariantChange.bind(this)
    );

    const mainProductForm = document.querySelector(
      "product-form:not([data-sticky-product-form])"
    );
    if (mainProductForm) {
      const observer = new MutationObserver(() => {
        this.syncWithMainForm();
      });

      observer.observe(mainProductForm, {
        attributes: true,
        subtree: true,
        attributeFilter: ["data-variant-id"],
      });
    }
  }

  handleVariantChange({ data: { html, variant } }) {
    if (!variant) return;
    this.updateVariant(variant, html);
  }

  syncWithMainForm() {
    const mainVariantInput = document.querySelector(
      'product-form:not([data-sticky-product-form]) input[name="id"]'
    );
    const stickyVariantInput =
      this.stickyButton.querySelector('input[name="id"]');

    if (mainVariantInput && stickyVariantInput) {
      stickyVariantInput.value = mainVariantInput.value;
    }
  }

  updateVariant(variant, html) {
    const variantInput = this.stickyButton.querySelector("[data-variant-id]");
    if (variantInput) {
      variantInput.value = variant.id;
    }

    const submitButton = this.stickyButton.querySelector(
      ".sticky-buy-button__submit"
    );
    if (submitButton) {
      if (variant.available) {
        submitButton.disabled = false;
        submitButton.querySelector("span").textContent =
          submitButton.dataset.addText || "Add to bag";
      } else {
        submitButton.disabled = true;
        submitButton.querySelector("span").textContent =
          submitButton.dataset.soldOutText || "Sold out";
      }
    }

    if (html) {
      this.updatePriceFromHTML(html);
    } else if (variant.price !== undefined) {
      this.updatePrice(variant);
    }

    if (variant.featured_image) {
      this.updateImage(variant.featured_image);
    }
  }

  updatePriceFromHTML(html) {
    const stickyPriceElement = this.stickyButton.querySelector(
      "[data-sticky-price]"
    );
    if (!stickyPriceElement) return;

    const sourcePrice =
      html.querySelector("product-info .price") ||
      html.querySelector(".product__price-container .price") ||
      html.querySelector(".price");

    if (sourcePrice) {
      stickyPriceElement.innerHTML = sourcePrice.innerHTML;
    }
  }

  updatePrice(variant) {
    const priceElement = this.stickyButton.querySelector("[data-sticky-price]");
    if (!priceElement) return;

    const locale = document.documentElement.lang || "de";
    const currency = window.Shopify?.currency?.active || "EUR";

    const formatMoney = (cents) => {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(cents / 100);
    };

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const discountPercentage = Math.round(
        ((variant.compare_at_price - variant.price) /
          variant.compare_at_price) *
          100
      );

      priceElement.innerHTML = `
        <div class="price__container">
          <span class="price__regular">
            <span class="price-item price-item--regular">
              ${formatMoney(variant.price)}
            </span>
          </span>
          <span class="price__sale">
            <span class="price__compare-at--rrp">
              <span class="price__rrp-label">RRP</span>
              <s class="price-item price-item--compare">
                ${formatMoney(variant.compare_at_price)}
              </s>
            </span>
            <span class="price-item__discount badge price__discount-badge">-${discountPercentage}%</span>
          </span>
        </div>
      `;
    } else {
      priceElement.innerHTML = `
        <span class="price__regular">
          <span class="price-item price-item--regular">
            ${formatMoney(variant.price)}
          </span>
        </span>
      `;
    }
  }

  updateImage(imageUrl) {
    const imageElement = this.stickyButton.querySelector(
      ".sticky-buy-button__image img"
    );
    if (imageElement && imageUrl) {
      imageElement.src = imageUrl;
    }
  }

  setupFormHandler() {
    // Form submission is handled by the product-form custom element
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new StickyBuyButton();
  });
} else {
  new StickyBuyButton();
}
